// devAuth.ts — username/password login against the DRF SimpleJWT endpoint
// (/api/identity/token/). Tokens live in localStorage so a page refresh stays
// logged in. This is the seam used when MSAL isn't configured but we still
// want to talk to the real backend.

const ACCESS_KEY = 'piq.devAuth.access';
const REFRESH_KEY = 'piq.devAuth.refresh';
const USER_KEY = 'piq.devAuth.username';

export interface DevSession {
  username: string;
  access: string;
}

export function getAccessToken(): string | null {
  return localStorage.getItem(ACCESS_KEY);
}

/** Decode a JWT's `exp` (epoch seconds) without verifying — for proactive refresh only. */
function tokenExpiry(token: string): number | null {
  try {
    const payload = JSON.parse(atob(token.split('.')[1] ?? '')) as { exp?: number };
    return typeof payload.exp === 'number' ? payload.exp : null;
  } catch {
    return null;
  }
}

/** Access token guaranteed fresh: if the stored one has expired (or is within a 30s skew),
 * refresh BEFORE returning it. This is what the API client uses as its token provider, so
 * cold-load queries never fire with a stale token — avoiding the 401-then-refresh burst. */
export async function getFreshAccessToken(): Promise<string | null> {
  const token = getAccessToken();
  if (!token) return null;
  const exp = tokenExpiry(token);
  if (exp !== null && Date.now() / 1000 >= exp - 30) {
    return refreshAccessToken();
  }
  return token;
}

export function getSession(): DevSession | null {
  const access = getAccessToken();
  const username = localStorage.getItem(USER_KEY);
  if (!access || !username) return null;
  return { access, username };
}

export function clearSession(): void {
  localStorage.removeItem(ACCESS_KEY);
  localStorage.removeItem(REFRESH_KEY);
  localStorage.removeItem(USER_KEY);
}

export async function devLogin(username: string, password: string): Promise<DevSession> {
  const base = (import.meta.env.VITE_API_BASE_URL ?? '').replace(/\/?$/, '');
  const res = await fetch(`${base}/api/identity/token/`, {
    method: 'POST',
    headers: { 'Content-Type': 'application/json', Accept: 'application/json' },
    body: JSON.stringify({ username, password }),
  });
  if (!res.ok) {
    let detail = `Login failed (${res.status})`;
    try {
      const body = (await res.json()) as { detail?: string };
      if (body?.detail) detail = body.detail;
    } catch {
      /* ignore */
    }
    throw new Error(detail);
  }
  const data = (await res.json()) as { access: string; refresh?: string };
  localStorage.setItem(ACCESS_KEY, data.access);
  if (data.refresh) localStorage.setItem(REFRESH_KEY, data.refresh);
  localStorage.setItem(USER_KEY, username);
  return { username, access: data.access };
}

/** Attempt to refresh the access token using the stored refresh token.
 *
 * Single-flight: refresh tokens are single-use (rotation + blacklist), so two
 * parallel 401s must share one refresh call — the loser of a race would post
 * an already-blacklisted token and wrongly clear the session. */
let refreshInFlight: Promise<string | null> | null = null;

export async function refreshAccessToken(): Promise<string | null> {
  if (!refreshInFlight) {
    refreshInFlight = doRefresh().finally(() => {
      refreshInFlight = null;
    });
  }
  return refreshInFlight;
}

async function doRefresh(): Promise<string | null> {
  const refresh = localStorage.getItem(REFRESH_KEY);
  if (!refresh) return null;
  const accessBefore = localStorage.getItem(ACCESS_KEY);
  const base = (import.meta.env.VITE_API_BASE_URL ?? '').replace(/\/?$/, '');
  try {
    const res = await fetch(`${base}/api/identity/token/refresh/`, {
      method: 'POST',
      headers: { 'Content-Type': 'application/json', Accept: 'application/json' },
      body: JSON.stringify({ refresh }),
    });
    if (!res.ok) {
      // The backend rotates + blacklists refresh tokens, so another tab/reload that refreshed
      // first will have invalidated the copy we just sent. If that context already stored a
      // fresh access token, adopt it instead of logging the user out (avoids the 401-storm
      // bounce-to-login on concurrent refreshes).
      const accessNow = localStorage.getItem(ACCESS_KEY);
      if (accessNow && accessNow !== accessBefore) return accessNow;
      clearSession();
      return null;
    }
    const data = (await res.json()) as { access: string; refresh?: string };
    localStorage.setItem(ACCESS_KEY, data.access);
    // the backend rotates refresh tokens (old one is blacklisted) — keep
    // the replacement or the next refresh fails with 401
    if (data.refresh) localStorage.setItem(REFRESH_KEY, data.refresh);
    return data.access;
  } catch {
    return null;
  }
}
